import {
  BodyBox,
  CellValues,
  ColumnHeading,
  FilterControlsConfig,
  MultiSelectOption,
  Notice,
  PaginatedDataTable,
  SelectOption,
  TableCellStylesFn,
  Text,
  useVisibleHeadings,
} from '@lbcamden/lib-react';
import {
  formatDMY,
  getSupportNeedViewRiskFactorKey,
  riskFactorDefs,
  ViewRiskFactorKey,
} from '@lbcamden/shared-utils';
import { FunctionComponent, useCallback, useMemo, useState } from 'react';

import { pcfraReviewDateFilterValueDisplayValues } from '~/client/config/display-values/residentsReport';
import { routes } from '~/client/config/routes';
import {
  useGlobalStore,
  useReferenceListDisplayValue,
  useReferenceListItemsByBaseType,
  useReferenceListSelectOptions,
} from '~/client/hooks/storeHooks';
import { ClientGlobalStore } from '~/client/store/tinybase/ClientGlobalStore';
import { toPcrfaPropertyPage } from '~/client/utils/navigation';
import { Property } from '~/common/types/buildings';
import { PcfraStatus, PersonAttributeCategoryCode, RpeepStatus } from '~/common/types/people';
import { ResidentRecord, ResidentsReportFilters } from '~/common/types/residentsReport';
import { selectOptionsFromDisplayValues } from '~/common/utils/data';

import { SectionContainer } from '../../common/SectionContainer';
import { PageBreadcrumbs } from '../../shared/PageBreadcrumbs';
import { checkHasStaleData, getReviewDateTextColour, getRpeepStatusColour } from './helpers';
import { ResidentsReportColumnId } from './types';
import { useResidentsReport } from './useResidentsReport';

const title = 'All residents';

const pageSize = 12;

const headings: ColumnHeading<ResidentsReportColumnId>[] = [
  { id: 'fullName', content: 'Name' },
  { id: 'propertyId', content: 'Address' },
  { id: 'floorLevel', content: 'Floor level' },
  { id: 'riskFactorsCount', content: 'Risk factors' },
  { id: 'pcfraStatus', content: 'PCFRA status' },
  { id: 'pcfraReviewDate', content: 'PCFRA review date' },
  { id: 'rpeepStatus', content: 'RPEEP status' },
];

const riskFactorOptions: MultiSelectOption<ViewRiskFactorKey>[] = Object.entries(
  riskFactorDefs,
).map(([key, { label }]) => ({
  id: key,
  value: key as ViewRiskFactorKey,
  label,
}));

const getFilterConfigs = (
  referenceListSelectOptions: ClientGlobalStore['referenceListSelectOptions'],
  supportNeedRiskFactorOptions: SelectOption[],
): FilterControlsConfig<ResidentsReportFilters> => [
  [
    {
      type: 'multi-select',
      key: 'riskFactors',
      label: 'Risk factors',
      options: [...supportNeedRiskFactorOptions, ...riskFactorOptions],
    },
    {
      type: 'multi-select',
      key: 'pcfraStatus',
      label: 'PCFRA status',
      options: [
        { id: 'none', label: 'No status', value: PcfraStatus.None },
        ...referenceListSelectOptions('PCFRA_STATUS'),
      ],
    },
    {
      type: 'multi-select',
      key: 'pcfraReviewDate',
      label: 'PCFRA review date',
      options: selectOptionsFromDisplayValues(pcfraReviewDateFilterValueDisplayValues),
    },
    {
      type: 'multi-select',
      key: 'rpeepStatus',
      label: 'RPEEP status',
      options: [
        { id: 'none', label: 'No status', value: RpeepStatus.None },
        ...referenceListSelectOptions('PCFRA_PEEP_STATUS'),
      ],
    },
  ],
  {
    type: 'text',
    key: 'fullName',
    label: 'Search by name',
    debounceWaitMs: 350,
  },
];

const getMapResidentToRow =
  (
    getPropertyFromId: (propertyId: number) => Property | undefined,
    referenceListDisplayValue: ClientGlobalStore['referenceListDisplayValue'],
  ) =>
  ({ pcfraReviewDate, ...resident }: ResidentRecord): CellValues<ResidentsReportColumnId> => ({
    fullName: resident.fullName,
    propertyId: getPropertyFromId(resident.propertyId)?.address,
    floorLevel: referenceListDisplayValue('FLOORLEVEL', resident.floorLevel || ''),
    riskFactorsCount: resident.riskFactorsCount,
    pcfraStatus: (
      <Text $color={resident.pcfraStatus === PcfraStatus.Active ? 'success' : 'text'}>
        {referenceListDisplayValue('PCFRA_STATUS', resident.pcfraStatus || '')}
      </Text>
    ),
    pcfraReviewDate: (
      <Text $color={getReviewDateTextColour(pcfraReviewDate)}>
        {pcfraReviewDate && formatDMY(pcfraReviewDate)}
      </Text>
    ),
    rpeepStatus: (
      <Text $color={getRpeepStatusColour(resident.rpeepStatus)}>
        {referenceListDisplayValue('PCFRA_PEEP_STATUS', resident.rpeepStatus || '')}
      </Text>
    ),
  });

const cellStyles: TableCellStylesFn<ResidentsReportColumnId> = (columnId) => [
  { width: 60 },
  {
    fullName: { width: 125 },
    propertyId: { width: 150 },
    riskFactorsCount: { width: 40, maxWidth: 70 },
  }[columnId as string],
];

export const ResidentsReportPage: FunctionComponent = () => {
  const globalStore = useGlobalStore();
  const referenceListDisplayValue = useReferenceListDisplayValue();
  const referenceListSelectOptions = useReferenceListSelectOptions();
  const riskFactorAttributeReferenceList = useReferenceListItemsByBaseType(
    PersonAttributeCategoryCode.SupportNeedRiskFactor,
  );

  const [hasStaleData, setHasStaleData] = useState(false);

  const getPropertyFromId = useCallback(
    (propertyId: number) => globalStore?.get('properties', propertyId) as Property | undefined,
    [globalStore],
  );

  const visibleHeadings = useVisibleHeadings(headings, {
    xs: headings.slice(0, 2),
    sm: headings.slice(0, 2),
    md: headings.slice(0, 4),
  });

  const supportNeedRiskFactorOptions = useMemo<SelectOption[]>(
    () =>
      riskFactorAttributeReferenceList.map(({ code }) => ({
        id: code,
        value: getSupportNeedViewRiskFactorKey(code),
        label: referenceListDisplayValue('PERSON_ATTRIBUTE', code),
      })),
    [referenceListDisplayValue, riskFactorAttributeReferenceList],
  );

  const filterConfigs = useMemo(
    () => getFilterConfigs(referenceListSelectOptions, supportNeedRiskFactorOptions),
    [referenceListSelectOptions, supportNeedRiskFactorOptions],
  );

  const mapResidentToRow = useMemo(
    () => getMapResidentToRow(getPropertyFromId, referenceListDisplayValue),
    [getPropertyFromId, referenceListDisplayValue],
  );

  const onClickRow = ({ propertyId, personId }: ResidentRecord) =>
    window.open(toPcrfaPropertyPage(propertyId, personId), '_blank');

  const onResultsChange = (results: ResidentRecord[]) =>
    setHasStaleData(checkHasStaleData(results));

  return (
    <BodyBox>
      <PageBreadcrumbs
        css={{ marginBottom: 0 }}
        links={[{ name: 'Home', link: routes.home }, { name: title }]}
      />
      <SectionContainer title={title}>
        {hasStaleData && (
          <Notice
            css={(theme) => ({ margin: theme.spacing([4, 0]) })}
            type="warning"
            title="Potentially stale data"
          >
            There may have been a problem fetching the most up-to-date information for this report.
            Please double-check any details you may be relying on for critical decisions, and if
            this message continues, please report the problem to your manager or the IT department.
          </Notice>
        )}
        <PaginatedDataTable
          headings={visibleHeadings}
          filterConfigs={filterConfigs}
          mapItemToRow={mapResidentToRow}
          cellStyles={cellStyles}
          pageSize={pageSize}
          defaultSort={{ field: 'riskFactorsCount', direction: 'desc' }}
          usePaginatedData={useResidentsReport}
          onClickRow={onClickRow}
          onResultsChange={onResultsChange}
        />
      </SectionContainer>
    </BodyBox>
  );
};
